import { useMemo } from 'react';

interface BiomarkerCardProps {
  count: number;
  label: string;
  color: string;
  totalDots?: number;
  onViewDetails?: () => void;
  variant?: 'dark' | 'light';
  alignLeft?: boolean;
  compactDots?: boolean;
}

export function BiomarkerCard({
  count,
  label,
  color,
  totalDots = 50,
  onViewDetails,
  variant = 'dark',
  alignLeft = false,
  compactDots = false,
}: BiomarkerCardProps) {
  const dots = useMemo(() => Array.from({ length: totalDots }), [totalDots]);

  const variantStyles = {
    dark: {
      container: 'bg-[rgba(255,255,255,0.08)] backdrop-blur-sm',
      number: 'text-white',
      label: 'text-gray-300',
      button: 'bg-white/20 text-white hover:bg-white/30',
      emptyDot: 'bg-gray-600',
    },
    light: {
      container: 'bg-white',
      number: 'text-gray-900',
      label: 'text-gray-600',
      button: 'bg-gray-900/10 text-gray-900 hover:bg-gray-900/20',
      emptyDot: 'bg-gray-200',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div
      className={`rounded-lg ${styles.container} flex flex-col items-center px-2 pt-2 pb-4 ${alignLeft ? 'md:items-start' : ''} md:p-6`}
    >
      <div
        className={`mb-2 flex w-full justify-center md:mb-2 ${alignLeft ? 'md:justify-between' : 'md:justify-center'}`}
      >
        <div className={`text-center ${alignLeft ? 'md:text-left' : ''}`}>
          <div className={`mb-0 text-xl leading-tight font-black ${styles.number} md:text-5xl md:leading-none`}>
            {count}
          </div>
          <div className={`text-xs ${styles.label} md:text-sm`}>{label}</div>
        </div>
        {onViewDetails && alignLeft && (
          <button
            onClick={onViewDetails}
            className={`hidden h-10 w-10 shrink-0 items-center justify-center rounded-full ${styles.button} transition-colors md:flex`}
            aria-label={`View ${label} biomarkers`}
          >
            →
          </button>
        )}
      </div>
      {/* Grid of biomarker indicators */}
      <div
        className={`flex flex-wrap gap-x-0.5 gap-y-0.5 md:max-w-[300px] md:gap-x-2 md:gap-y-1.5 ${compactDots ? 'max-w-[58px]' : 'max-w-[85px]'}`}
      >
        {dots.map((_, i) => (
          <div key={i} className={`h-1 w-1 md:h-2 md:w-2 ${i < count ? color : styles.emptyDot}`} />
        ))}
      </div>
    </div>
  );
}
